// src/support/SupportManagement.js
import React, { useState, useEffect, useCallback } from 'react';
import { adminFetch } from '../utils/adminAuth';
import './SupportManagement.css';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:3002';

const STATUS_OPTIONS = ['open', 'in_progress', 'resolved', 'closed'];
const PRIORITY_OPTIONS = ['low', 'normal', 'high', 'urgent'];
const CATEGORY_OPTIONS = ['', 'refund', 'technical', 'event_inquiry', 'account', 'other'];

function timeAgo(dateStr) {
  if (!dateStr) return '';
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 30) return `${days}d ago`;
  return new Date(dateStr).toLocaleDateString();
}

function StatusBadge({ status }) {
  return <span className={`sm-badge sm-status-${status}`}>{(status || '').replace('_', ' ')}</span>;
}

function PriorityBadge({ priority }) {
  if (!priority || priority === 'normal' || priority === 'low') return null;
  return <span className={`sm-badge sm-priority-${priority}`}>{priority}</span>;
}

export default function SupportManagement({ onBack }) {
  const [tickets, setTickets] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [comments, setComments] = useState([]);
  const [detailLoading, setDetailLoading] = useState(false);
  const [replyText, setReplyText] = useState('');
  const [sending, setSending] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [actionMsg, setActionMsg] = useState(null);

  // Filters
  const [filterStatus, setFilterStatus] = useState('');
  const [filterCategory, setFilterCategory] = useState('');
  const [filterPriority, setFilterPriority] = useState('');
  const [search, setSearch] = useState('');

  // Pagination
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState(null);

  const showMsg = (text, isError = false) => {
    setActionMsg({ text, isError });
    setTimeout(() => setActionMsg(null), 3000);
  };

  const loadTickets = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      if (filterStatus) params.set('status', filterStatus);
      if (filterCategory) params.set('category', filterCategory);
      if (filterPriority) params.set('priority', filterPriority);
      params.set('page', page);
      params.set('limit', '25');
      const res = await adminFetch(`${API_BASE}/api/support/tickets?${params}`);
      const data = await res.json();
      if (data.success) {
        setTickets(data.tickets || []);
        setPagination(data.pagination || null);
      } else {
        setError(data.error || 'Failed to load tickets');
      }
    } catch (err) {
      setError(err.message || 'Failed to load tickets');
    } finally {
      setLoading(false);
    }
  }, [filterStatus, filterCategory, filterPriority, page]);

  const loadStats = useCallback(async () => {
    try {
      const res = await adminFetch(`${API_BASE}/api/support/stats`);
      const data = await res.json();
      if (data.success) setStats(data.data);
    } catch {
      // stats are non-critical
    }
  }, []);

  useEffect(() => { loadTickets(); }, [loadTickets]);
  useEffect(() => { loadStats(); }, [loadStats]);

  const loadDetail = async (ticket) => {
    setSelectedTicket(ticket);
    setComments([]);
    setDetailLoading(true);
    try {
      const res = await adminFetch(`${API_BASE}/api/support/tickets/${ticket.id}?userAddress=${ticket.userAddress}`);
      const data = await res.json();
      if (data.success) {
        setSelectedTicket(data.ticket);
        setComments(data.comments || []);
      } else {
        showMsg(data.error || 'Failed to load ticket detail', true);
      }
    } catch (err) {
      showMsg(err.message || 'Failed to load ticket', true);
    } finally {
      setDetailLoading(false);
    }
  };

  const handleStatusChange = async (ticketId, newStatus) => {
    setActionLoading(true);
    try {
      const res = await adminFetch(`${API_BASE}/api/support/tickets/${ticketId}/status`, {
        method: 'PATCH',
        body: JSON.stringify({ status: newStatus }),
      });
      const data = await res.json();
      if (data.success) {
        if (selectedTicket?.id === ticketId) {
          setSelectedTicket((prev) => ({ ...prev, status: newStatus }));
        }
        showMsg(`Status changed to ${newStatus.replace('_', ' ')}`);
        await loadTickets();
        await loadStats();
      } else {
        showMsg(data.error || 'Failed to update status', true);
      }
    } catch (err) {
      showMsg(err.message || 'Failed to update status', true);
    } finally {
      setActionLoading(false);
    }
  };

  const handlePriorityChange = async (ticketId, newPriority) => {
    setActionLoading(true);
    try {
      const res = await adminFetch(`${API_BASE}/api/support/tickets/${ticketId}/priority`, {
        method: 'PATCH',
        body: JSON.stringify({ priority: newPriority }),
      });
      const data = await res.json();
      if (data.success) {
        if (selectedTicket?.id === ticketId) {
          setSelectedTicket((prev) => ({ ...prev, priority: newPriority }));
        }
        showMsg(`Priority changed to ${newPriority}`);
        await loadTickets();
      } else {
        showMsg(data.error || 'Failed to update priority', true);
      }
    } catch (err) {
      showMsg(err.message || 'Failed to update priority', true);
    } finally {
      setActionLoading(false);
    }
  };

  const handleAssign = async (ticketId) => {
    setActionLoading(true);
    try {
      const res = await adminFetch(`${API_BASE}/api/support/tickets/${ticketId}/assign`, {
        method: 'PATCH',
        body: JSON.stringify({}),
      });
      const data = await res.json();
      if (data.success) {
        if (selectedTicket?.id === ticketId) {
          setSelectedTicket((prev) => ({
            ...prev,
            assignedTo: data.ticket?.assignedTo,
            status: data.ticket?.status || prev.status,
          }));
        }
        showMsg('Ticket assigned to you');
        await loadTickets();
        await loadStats();
      } else {
        showMsg(data.error || 'Failed to assign ticket', true);
      }
    } catch (err) {
      showMsg(err.message || 'Failed to assign ticket', true);
    } finally {
      setActionLoading(false);
    }
  };

  const handleAdminReply = async () => {
    if (!replyText.trim() || !selectedTicket) return;
    setSending(true);
    try {
      const res = await adminFetch(`${API_BASE}/api/support/tickets/${selectedTicket.id}/comments`, {
        method: 'POST',
        body: JSON.stringify({ comment: replyText.trim() }),
      });
      const data = await res.json();
      if (data.success) {
        setReplyText('');
        showMsg('Reply sent');
        await loadDetail(selectedTicket);
      } else {
        showMsg(data.error || 'Failed to send reply', true);
      }
    } catch (err) {
      showMsg(err.message || 'Failed to send reply', true);
    } finally {
      setSending(false);
    }
  };

  const filtered = tickets.filter((t) => {
    if (search) {
      const q = search.toLowerCase();
      if (!t.subject?.toLowerCase().includes(q) && !t.userAddress?.toLowerCase().includes(q)) return false;
    }
    return true;
  });

  const byStatus = stats?.byStatus || {};

  // ── Detail view ──
  if (selectedTicket) {
    return (
      <div className="support-mgmt">
        {actionMsg && (
          <div className={`sm-toast ${actionMsg.isError ? 'sm-toast-error' : 'sm-toast-success'}`}>
            {actionMsg.text}
          </div>
        )}
        <button className="sm-back-btn" onClick={() => { setSelectedTicket(null); loadTickets(); }}>&larr; Back to tickets</button>

        <div className="sm-detail-card">
          <div className="sm-detail-header">
            <h3>{selectedTicket.subject}</h3>
            <div className="sm-detail-badges">
              <StatusBadge status={selectedTicket.status} />
              <PriorityBadge priority={selectedTicket.priority} />
              <span className="sm-category">{selectedTicket.category?.replace('_', ' ')}</span>
            </div>
          </div>

          <div className="sm-detail-body">
            <p className="sm-detail-desc">{selectedTicket.description}</p>
            <div className="sm-detail-meta">
              <span>User: {selectedTicket.userAddress?.slice(0, 6)}...{selectedTicket.userAddress?.slice(-4)}</span>
              <span>Created: {timeAgo(selectedTicket.createdAt)}</span>
              {selectedTicket.email && <span>Email: {selectedTicket.email}</span>}
              {selectedTicket.assignedTo && (
                <span>Assigned: {selectedTicket.assignedTo.slice(0, 6)}...{selectedTicket.assignedTo.slice(-4)}</span>
              )}
              {selectedTicket.resolvedAt && <span>Resolved: {timeAgo(selectedTicket.resolvedAt)}</span>}
            </div>
          </div>

          <div className="sm-admin-actions">
            <div className="sm-action-group">
              <label>Status</label>
              <select value={selectedTicket.status} disabled={actionLoading}
                onChange={(e) => handleStatusChange(selectedTicket.id, e.target.value)}>
                {STATUS_OPTIONS.map((s) => <option key={s} value={s}>{s.replace('_', ' ')}</option>)}
              </select>
            </div>
            <div className="sm-action-group">
              <label>Priority</label>
              <select value={selectedTicket.priority || 'normal'} disabled={actionLoading}
                onChange={(e) => handlePriorityChange(selectedTicket.id, e.target.value)}>
                {PRIORITY_OPTIONS.map((p) => <option key={p} value={p}>{p}</option>)}
              </select>
            </div>
            {!selectedTicket.assignedTo && (
              <button className="sm-assign-btn" onClick={() => handleAssign(selectedTicket.id)} disabled={actionLoading}>
                Assign to me
              </button>
            )}
          </div>

          <div className="sm-comments-section">
            <h4>Conversation</h4>
            {detailLoading ? (
              <p className="sm-loading-text">Loading...</p>
            ) : comments.length === 0 ? (
              <p className="sm-no-comments">No replies yet.</p>
            ) : (
              <div className="sm-comment-thread">
                {comments.map((c) => (
                  <div key={c.id} className={`sm-comment ${c.authorType}`}>
                    <div className="sm-comment-author">{c.authorType === 'admin' ? 'Admin' : 'User'}</div>
                    <div className="sm-comment-text">{c.comment}</div>
                    <div className="sm-comment-time">{timeAgo(c.createdAt)}</div>
                  </div>
                ))}
              </div>
            )}

            {selectedTicket.status !== 'closed' && (
              <div className="sm-reply-form">
                <textarea value={replyText} onChange={(e) => setReplyText(e.target.value)}
                  placeholder="Reply as admin..."
                  onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleAdminReply(); } }}
                />
                <button className="sm-send-btn" onClick={handleAdminReply} disabled={sending || !replyText.trim()}>
                  {sending ? '...' : 'Send as Admin'}
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  // ── List view ──
  return (
    <div className="support-mgmt">
      {actionMsg && (
        <div className={`sm-toast ${actionMsg.isError ? 'sm-toast-error' : 'sm-toast-success'}`}>
          {actionMsg.text}
        </div>
      )}

      <div className="sm-header">
        {onBack && <button className="sm-back-btn" onClick={onBack}>&larr; Back</button>}
        <h2>Support Management</h2>
      </div>

      {stats && (
        <div className="sm-stats-row">
          <div className="sm-stat-card">
            <div className="sm-stat-num">{byStatus.open || 0}</div>
            <div className="sm-stat-label">Open</div>
          </div>
          <div className="sm-stat-card">
            <div className="sm-stat-num">{byStatus.in_progress || 0}</div>
            <div className="sm-stat-label">In Progress</div>
          </div>
          <div className="sm-stat-card">
            <div className="sm-stat-num">{byStatus.resolved || 0}</div>
            <div className="sm-stat-label">Resolved</div>
          </div>
          <div className="sm-stat-card">
            <div className="sm-stat-num">{byStatus.closed || 0}</div>
            <div className="sm-stat-label">Closed</div>
          </div>
          <div className="sm-stat-card">
            <div className="sm-stat-num">{stats.total || 0}</div>
            <div className="sm-stat-label">Total</div>
          </div>
          <div className="sm-stat-card">
            <div className="sm-stat-num">{stats.unassigned || 0}</div>
            <div className="sm-stat-label">Unassigned</div>
          </div>
        </div>
      )}

      {error && <div className="sm-error">{error}</div>}

      <div className="sm-filters">
        <input className="sm-search" type="text" placeholder="Search subject or address..."
          value={search} onChange={(e) => setSearch(e.target.value)} />
        <select className="sm-filter-select" value={filterStatus} onChange={(e) => { setFilterStatus(e.target.value); setPage(1); }}>
          <option value="">All Status</option>
          {STATUS_OPTIONS.map((s) => <option key={s} value={s}>{s.replace('_', ' ')}</option>)}
        </select>
        <select className="sm-filter-select" value={filterCategory} onChange={(e) => { setFilterCategory(e.target.value); setPage(1); }}>
          <option value="">All Categories</option>
          {CATEGORY_OPTIONS.filter(Boolean).map((c) => <option key={c} value={c}>{c.replace('_', ' ')}</option>)}
        </select>
        <select className="sm-filter-select" value={filterPriority} onChange={(e) => { setFilterPriority(e.target.value); setPage(1); }}>
          <option value="">All Priority</option>
          {PRIORITY_OPTIONS.map((p) => <option key={p} value={p}>{p}</option>)}
        </select>
      </div>

      {loading ? (
        <div className="sm-loading-text">Loading tickets...</div>
      ) : filtered.length === 0 ? (
        <div className="sm-empty">No tickets found.</div>
      ) : (
        <>
          <div className="sm-ticket-table">
            <div className="sm-table-header">
              <span className="sm-col-subject">Subject</span>
              <span className="sm-col-user">User</span>
              <span className="sm-col-status">Status</span>
              <span className="sm-col-priority">Priority</span>
              <span className="sm-col-category">Category</span>
              <span className="sm-col-date">Created</span>
            </div>
            {filtered.map((t) => (
              <div key={t.id} className={`sm-table-row ${!t.assignedTo ? 'sm-unassigned' : ''}`} onClick={() => loadDetail(t)}>
                <span className="sm-col-subject">{t.subject}</span>
                <span className="sm-col-user">{t.userAddress?.slice(0, 6)}...{t.userAddress?.slice(-4)}</span>
                <span className="sm-col-status"><StatusBadge status={t.status} /></span>
                <span className="sm-col-priority"><PriorityBadge priority={t.priority} /></span>
                <span className="sm-col-category">{t.category?.replace('_', ' ')}</span>
                <span className="sm-col-date">{timeAgo(t.createdAt)}</span>
              </div>
            ))}
          </div>

          {pagination && pagination.pages > 1 && (
            <div className="sm-pagination">
              <button disabled={page <= 1} onClick={() => setPage(page - 1)}>Prev</button>
              <span>Page {pagination.page} of {pagination.pages} ({pagination.total} tickets)</span>
              <button disabled={page >= pagination.pages} onClick={() => setPage(page + 1)}>Next</button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
